
























































































































































































import { Component, Mixins } from "vue-property-decorator";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import CountriesStore from "@/mixins/constants/countriesStore";
import LanguagesStore from "@/mixins/constants/languagesStore";
import HasValidation from "@/mixins/HasValidation";
import BrandMixin from "@/mixins/brandMixin";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ImageObjectTypes } from "@/models/images";
import { ImageUploadTypes } from "@/data/constants";

// Types
import { IUploadFile } from "@/models/uploadFiles";
import { IBrandSettingsForm } from "@/models/brands";

@Component({
  components: {
    "brand-languages": () =>
      import("@/components/app/admin/settings/brands/brandLanguages.vue"),
    "file-upload-field": () =>
      import("@/components/app/global/fields/fileUploadField.vue")
  }
})
export default class BrandSettings extends Mixins(
  RequestPromises,
  CountriesStore,
  LanguagesStore,
  HasValidation,
  BrandMixin
) {
  // Data

  public form = {} as IBrandSettingsForm;
  public formClone = {} as IBrandSettingsForm;

  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  // Brand Imaginery

  public imageType = ImageObjectTypes.BRAND;
  public faviconType = ImageObjectTypes.BRAND_FAVICON;
  public image = {} as IUploadFile;
  public favicon = {} as IUploadFile;
  public imageUploadTypes = ImageUploadTypes;

  // Methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await Promise.all([this.getCountries(), this.getLanguages()]);
      this.fillForm();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
      this.addBrandName();
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public fillForm() {
    this.form = this.$_.merge(
      {},
      this.$_.pick(this.brand, [
        "name",
        "code",
        "country_id",
        "language_id",
        "style"
      ]),
      {
        supported_languages: this.$_.orderBy(
          this.$_.map(
            this.$_.get(this.brand, "languages", []),
            language => language.id
          )
        ),
        logo: this.$_.get(this.brand, "image.hash", null),
        favicon: this.$_.get(this.brand, "favicon.hash", null)
      }
    );
    this.formClone = this.$_.cloneDeep(this.form);
  }

  public addBrandName() {
    this.$store.commit("ui/breadcrumb/add", {
      key: "adminBrand",
      value: this.brand.name
    });
  }

  public refreshBrand() {
    return this.$store.dispatch("brand/get", { ignoreStored: true });
  }

  public get isFormChanged() {
    return (
      !this.$_.isEqual(
        this.$_.omit(this.form, ["supported_languages"]),
        this.$_.omit(this.formClone, ["supported_languages"])
      ) ||
      this.$_.sortBy(this.form.supported_languages).join("_") !==
        this.$_.sortBy(this.formClone.supported_languages).join("_")
    );
  }

  public get formIsDisabled() {
    return (
      this.isProcessing ||
      !this.isFormChanged ||
      this.hasErrors ||
      this.isLoading
    );
  }

  public get canUpdateBrand() {
    return this.$userCan("update_brand");
  }

  public revertToInitialState() {
    this.form = this.$_.cloneDeep(this.formClone);
  }

  public async validateBeforeSubmit() {
    if (this.canUpdateBrand) {
      this.isProcessing = true;

      try {
        if (!(await this.validate())) {
          throw this.$t("_sentence.form_is_not_valid");
        }

        await Promise.all([this.manageBrandLogo(), this.manageBrandFavicon()]);
        await this.updateBrand();

        await Promise.all([
          this.refreshBrand(),
          // Here we refresh user data to cover brand object changes (eg. name)
          this.$store.dispatch("auth/admin/getUser")
        ]);
        this.fillForm();

        this.$store.dispatch("toast/show", {
          message: this.$t("_.object_successfully_updated", {
            object: this.$t("_.brand")
          })
        });
      } catch (error) {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      } finally {
        this.isProcessing = false;
      }
    }
  }

  public updateBrand() {
    return this.$store.dispatch(`data/${DataModules.BRANDS}/update`, {
      id: this.brandId,
      data: this.getNewValues()
    });
  }

  public manageBrandLogo() {
    if (this.form.logo === this.formClone.logo) {
      return Promise.resolve();
    }

    if (this.form.logo === null) {
      return this.deleteBrandImage();
    }

    return this.uploadBrandImage();
  }

  public manageBrandFavicon() {
    if (this.form.favicon === this.formClone.favicon) {
      return Promise.resolve();
    }

    if (this.form.favicon === null) {
      return this.deleteBrandFavicon();
    }

    return this.uploadBrandFavicon();
  }

  public async uploadBrandImage() {
    const data = new FormData();
    data.append("image", this.$_.get(this.image, "file.fileObj"));

    try {
      const result = await this.$store.dispatch(
        `data/${DataModules.IMAGES}/uploadImage`,
        {
          typeId: this.brandId,
          imageType: this.imageType,
          isDefault: true,
          data,
          onUploadProgress: progressEvent => {
            this.image = this.$_.merge(this.image, {
              progress: Math.floor(
                (progressEvent.loaded * 100) / progressEvent.total
              ),
              isUploading: true
            });
          },
          vm: this
        }
      );

      this.image = this.$_.merge(this.image, {
        progress: 0,
        isUploading: false,
        hash: result.value
      });
      return Promise.resolve();
    } catch (error) {
      const data = this.$_.get(error, "response.data.error.data", {});
      if (data.image) {
        data.logo = data.image;
        delete data.image;
      }
      throw error;
    }
  }

  public deleteBrandImage() {
    return this.$store.dispatch(`data/${DataModules.IMAGES}/deleteImage`, {
      typeId: this.brandId,
      imageType: this.imageType,
      hash: this.formClone.logo,
      vm: this
    });
  }

  public async uploadBrandFavicon() {
    const data = new FormData();
    data.append("image", this.$_.get(this.favicon, "file.fileObj"));

    try {
      const result = await this.$store.dispatch(
        `data/${DataModules.IMAGES}/uploadImage`,
        {
          typeId: this.brandId,
          imageType: this.faviconType,
          data,
          onUploadProgress: progressEvent => {
            this.favicon = this.$_.merge(this.favicon, {
              progress: Math.floor(
                (progressEvent.loaded * 100) / progressEvent.total
              ),
              isUploading: true
            });
          },
          vm: this
        }
      );

      this.favicon = this.$_.merge(this.favicon, {
        progress: 0,
        isUploading: false,
        hash: result.value
      });
      return Promise.resolve();
    } catch (error) {
      const data = this.$_.get(error, "response.data.error.data", {});
      if (data.image) {
        data.logo = data.image;
        delete data.image;
      }
      throw error;
    }
  }

  public deleteBrandFavicon() {
    return this.$store.dispatch(`data/${DataModules.IMAGES}/deleteImage`, {
      typeId: this.brandId,
      imageType: this.faviconType,
      hash: this.formClone.favicon,
      vm: this
    });
  }

  public getNewValues() {
    const newValues: any = this.$_.omitBy(this.form, (v, k) => {
      return this.formClone[k] === v;
    });

    delete newValues.logo;
    delete newValues.favicon;

    return newValues;
  }

  public imageDeleted() {
    this.image = {} as IUploadFile;
  }

  public imageAdded(image) {
    this.image = {
      file: image,
      isUploading: false,
      progress: 0,
      hash: null
    } as IUploadFile;
  }

  public faviconAdded(favicon) {
    this.favicon = {
      file: favicon,
      isUploading: false,
      progress: 0,
      hash: null
    } as IUploadFile;
  }

  public faviconDeleted() {
    this.favicon = {} as IUploadFile;
  }

  public guardFunctionalities = ["get_brand"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }
}
